import React, { useState } from 'react';

/**
 * AI Content Generation Panel
 * All content goes to DRAFT status - requires manual approval
 */
const AIContentGenerator = ({ onContentGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [generationType, setGenerationType] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const contentTypes = [
    {
      id: 'blog',
      name: 'Blog Post',
      icon: '📝',
      description: 'AI-generated blog article draft',
      color: 'from-blue-500 to-blue-600',
      service: 'AI Staff'
    },
    {
      id: 'social',
      name: 'Social Media Post',
      icon: '📱',
      description: 'Social media caption variations',
      color: 'from-purple-500 to-purple-600',
      service: 'AI Socials'
    },
    {
      id: 'image',
      name: 'Marketing Image',
      icon: '🎨',
      description: 'AI-generated marketing visual',
      color: 'from-pink-500 to-pink-600',
      service: 'Artistly'
    },
    {
      id: 'audio',
      name: 'Voice Content',
      icon: '🎙️',
      description: 'AI voiceover for social media',
      color: 'from-green-500 to-green-600',
      service: 'VoiceClone.ai'
    },
    {
      id: 'video',
      name: 'Marketing Video',
      icon: '🎬',
      description: 'Short AI-generated video',
      color: 'from-orange-500 to-orange-600',
      service: 'Video Express'
    },
    {
      id: 'email',
      name: 'Email Campaign',
      icon: '✉️',
      description: 'Email campaign draft',
      color: 'from-indigo-500 to-indigo-600',
      service: 'AI Staff'
    }
  ];

  const handleGenerateContent = async (type) => {
    setGenerating(true);
    setGenerationType(type.id);

    try {
      let endpoint = '';
      let payload = {};

      // Map content types to actual backend endpoints
      switch (type.id) {
        case 'blog':
          endpoint = `${BACKEND_URL}/api/crm/content/generate-blog`;
          payload = {
            topic: 'UK Grocery Price Savings Tips',
            price_data: {}
          };
          break;
        case 'social':
          endpoint = `${BACKEND_URL}/api/crm/content/generate-social`;
          payload = {
            platform: 'instagram',
            count: 2
          };
          break;
        case 'image':
          endpoint = `${BACKEND_URL}/api/crm/content/generate-image`;
          payload = {
            prompt: 'Professional marketing image for UK grocery savings app',
            style: 'modern'
          };
          break;
        case 'audio':
          // CloneVoice API - generate voiceover
          endpoint = `${BACKEND_URL}/api/crm/content/generate-audio`;
          payload = {
            text: 'Welcome to PricePAL Compare, where UK shoppers save hundreds on groceries',
            voice_style: 'professional'
          };
          break;
        case 'video':
          // Video Express - generate marketing video
          endpoint = `${BACKEND_URL}/api/crm/content/generate-video`;
          payload = {
            script: 'Introducing PricePAL Compare - Compare prices across UK retailers instantly',
            duration: 30
          };
          break;
        case 'email':
          endpoint = `${BACKEND_URL}/api/crm/content/generate-newsletter`;
          payload = {
            subscriber_segment: 'active_users',
            price_highlights: []
          };
          break;
        default:
          throw new Error('Unknown content type');
      }

      // Call the actual backend API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      // Format the response into our standard structure
      let generatedContent = {
        id: `draft_${Date.now()}`,
        type: type.id,
        title: `${type.name} - ${new Date().toLocaleDateString()}`,
        content: JSON.stringify(data, null, 2), // Store full API response
        service: type.service,
        status: 'draft',
        created_at: new Date().toISOString(),
        requires_review: true,
        api_response: data // Store original response
      };

      // Special handling for blog posts - extract properly formatted data
      if (type.id === 'blog' && data.blog_post) {
        generatedContent = {
          ...generatedContent,
          title: data.blog_post.title || 'AI Generated Blog Post',
          content: data.blog_post.content || '',
          excerpt: data.blog_post.excerpt || '',
          author: data.blog_post.author || 'PricePAL Compare AI',
          category: 'Money Saving Tips',
          image_url: data.blog_post.image_url || '',
          tags: data.blog_post.tags || [],
          is_placeholder: data.placeholder_warning ? true : false
        };
      }

      if (onContentGenerated) {
        onContentGenerated(generatedContent);
      }

      // Show success message
      alert(`✅ ${type.name} generated successfully!\n\nService: ${type.service}\nStatus: DRAFT - Requires Review`);
    } catch (error) {
      console.error('Error generating content:', error);
      alert(`❌ Failed to generate ${type.name}.\n\nError: ${error.message}\n\nPlease check that backend services are running.`);
    } finally {
      setGenerating(false);
      setGenerationType(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🤖 AI Content Generation
        </h2>
        <p className="text-gray-600">
          Generate content drafts with AI. All content requires manual review and approval.
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-2xl">⚠️</span>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Manual Review Required:</strong> All AI-generated content will be saved as DRAFT status and must be reviewed, edited, and manually approved before publishing.
            </p>
          </div>
        </div>
      </div>

      {/* Content Type Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contentTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => handleGenerateContent(type)}
            disabled={generating}
            className={`p-6 rounded-xl bg-gradient-to-br ${type.color} text-white hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
          >
            <div className="text-center">
              <div className="text-5xl mb-3">{type.icon}</div>
              <h3 className="text-lg font-bold mb-2">{type.name}</h3>
              <p className="text-sm opacity-90 mb-3">{type.description}</p>
              <div className="text-xs bg-white bg-opacity-20 rounded-full px-3 py-1 inline-block">
                {type.service}
              </div>
              {generating && generationType === type.id && (
                <div className="mt-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto"></div>
                  <p className="text-xs mt-2">Generating...</p>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Info Footer */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center text-sm">
          <div>
            <div className="text-gray-500">Status</div>
            <div className="font-semibold text-gray-900">Manual Mode</div>
          </div>
          <div>
            <div className="text-gray-500">Auto-Publishing</div>
            <div className="font-semibold text-red-600">DISABLED</div>
          </div>
          <div>
            <div className="text-gray-500">Approval Required</div>
            <div className="font-semibold text-green-600">YES</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIContentGenerator;
